import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useIdentity } from '@/entities/session';
import { BrandLoader } from '@/shared/ui/BrandLoader';

/* The screen a reader sees while the app finds out who they are: the logo animating, a thin sweep
 * and one line of status.
 *
 * **Shown only until `/v1/auth/me` settles, and never held for the animation.** The moment the
 * session read answers — either way — the surface fades out over 280 ms and is removed. Holding it
 * for the loop to finish would make every start a second slower for the sake of a logo.
 *
 * **A failed read still lets the app through.** "Could not reach Lumiq" is said for the length of
 * the fade, and then the shell renders and each screen reports its own failure in place, which says
 * far more than a full-screen error could.
 *
 * **Once per page load.** It sits in the shell, which stays mounted across navigation, and starts
 * gone when the identity is already cached — so moving between screens never shows it again.
 */

/** Slightly longer than the 280 ms fade, so the surface leaves after the fade has finished. */
const REMOVE_AFTER_MS = 300;

export function StartupSurface(): ReactElement | null {
  const { t } = useTranslation();
  const identity = useIdentity();
  const settled = !identity.isPending;
  const [gone, setGone] = useState(settled);

  useEffect(() => {
    if (!settled || gone) return undefined;
    const timer = window.setTimeout(() => {
      setGone(true);
    }, REMOVE_AFTER_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [settled, gone]);

  if (gone) return null;

  return (
    <div
      className="lq-boot"
      role="status"
      aria-live="polite"
      aria-label={t('boot.label')}
      data-leaving={settled ? 'true' : undefined}
    >
      <BrandLoader size="lg" />
      <div className="lq-boot__status">
        <span className="lq-boot__track" aria-hidden="true">
          <span className="lq-boot__bar" />
        </span>
        <span className="lq-boot__text">
          {identity.isError ? t('boot.unreachable') : t('boot.loading')}
        </span>
      </div>
    </div>
  );
}
